import { useEffect } from "react";

import useRoutes from "../../hooks/useRoutes";
import useRouter from "../../hooks/useRouter";
import useQueryState from "../../hooks/useQueryState";
import { useAuthToken } from "../../contexts/AuthTokenContext";
import { useGlobalContext } from "../../contexts/GlobalContext";
import { UNAUTHORIZED_EVENT } from "../../../utils/unauthorized";

// Bounces anyone without a token onto the login page, and anyone who already has one
// back off it.
export const useAuthRedirect = () => {
  const { token } = useAuthToken();
  const { location, pushRoute } = useRouter();
  const { routes } = useRoutes();
  const { pathname } = location;

  useEffect(() => {
    if (!token) {
      pushRoute(routes.login.url);
    } else if (pathname === routes.login.url) {
      pushRoute(routes.root.url);
    }
  }, [token, pathname, pushRoute, routes.login.url, routes.root.url]);
};

// The one listener for a 401 from either data layer: drops the dead token, which is what
// `useAuthRedirect` above watches, so the app lands on the login page.
export const useUnauthorizedRedirect = () => {
  const { clearToken } = useAuthToken();
  const { setGlobalContext } = useGlobalContext();

  useEffect(() => {
    const handleUnauthorized = () => {
      clearToken();
      setGlobalContext({ user: false });
    };

    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);

    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
  }, [clearToken, setGlobalContext]);
};

// Releases the header button lock once the new route is resolved, and keeps the page
// size the grids read out of the global context in step with the URL.
export const useGlobalContextSync = () => {
  const { isHeaderButtonActionDisabled, tablePerPage, setGlobalContext } = useGlobalContext();
  const { urlPaginationObject } = useQueryState();
  const { currentRoute } = useRoutes();
  const urlPerPage = urlPaginationObject.perPage;

  useEffect(() => {
    if (currentRoute && isHeaderButtonActionDisabled) {
      setGlobalContext({ isHeaderButtonActionDisabled: false });
    }
  }, [currentRoute, isHeaderButtonActionDisabled, setGlobalContext]);

  /**
   * The url is the only thing that can move the page size here, and it only moves it when it
   * actually carries one.
   *
   * This used to read `tablePerPage` straight out of localStorage during render and list it as a
   * dependency — while `GlobalContextProvider` rewrites that same key from the state this effect
   * sets. Reading your own write back a render later oscillates, and `JSON.stringify` dropping an
   * `undefined` value kept the two alternating states genuinely different, so it never converged:
   * every cold load burned ~25 nested renders and logged "Maximum update depth exceeded".
   *
   * Two things fix it. The current size comes from the context, which is the one place that owns
   * it (its initial state already merges localStorage, so nothing is lost). And a size is only
   * written when the url has one that differs — the old code wrote `urlPerPage || stored`, which
   * on a cold load was `undefined || undefined`, overwriting the default it had just been handed.
   */
  useEffect(() => {
    if (urlPerPage && urlPerPage !== tablePerPage) {
      setGlobalContext({ tablePerPage: urlPerPage });
    }
  }, [urlPerPage, tablePerPage, setGlobalContext]);
};
